import React, { useEffect } from 'react';
import { Styles } from '@t2c/shared-fe';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { CustomDialogContainer, CustomDialogTitle } from '../../Common/dialog';
import { CircularProgress } from '@mui/material';
import { Box } from '@mui/material';
import { AIMedia, AIMediaStatus, Status, WebSocketDataReturnType } from '../../@types';
import { useRouter } from 'next/router';
import { useAppContext } from '../../hooks';
import CustomizedProgressBars from '../../Common/ProgressBar';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { MediaStatus, VideoDetailsType } from '../../../t2c-fe/utils';
import { ProgressStuckNote } from '../../Common';

interface Props {
  shouldOpenDialog: boolean;
  handleCloseDialog: () => void;
  data: VideoDetailsType;
}

export const TalkingFaceProccessingModal: React.FC<Props> = ({
  shouldOpenDialog,
  handleCloseDialog,
  data,
}) => {
  const { push } = useRouter();
  const {
    websocket: { avatarVideoTaskMap, handleRemoveFromTaskMap },
  } = useAppContext();

  const taskData = avatarVideoTaskMap.get(data?.id);

  const cardProgress = taskData?.progress || 0;
  const isTaskSuccessful = taskData?.status === Status.Success;
  const isInQueue =
    !taskData || taskData?.status === Status.InQueue || taskData?.status === Status.NotStarted;
  const isTaskInProgress = taskData?.status === Status.InProgress;
  const isTaskFailed = taskData?.status === Status.Error || data?.status === MediaStatus.Error;

  const onCloseDialog = () => {
    handleCloseDialog();
    if (isTaskSuccessful || isTaskFailed) {
      handleRemoveFromTaskMap({ id: data?.id, type: WebSocketDataReturnType.AVATAR_VIDEO });
    }
  };

  useEffect(() => {
    if (isTaskSuccessful) {
      push(`video/${data?.id}`);
    }
  }, [isTaskSuccessful]);

  return (
    <CustomDialogContainer open={shouldOpenDialog} maxWidth="md">
      <CustomDialogTitle
        sx={styles.title}
        title={'Generating AI Avatar Video'}
        onClose={onCloseDialog}
      />
      <DialogContent dividers sx={styles.dialogContent}>
        <>
          {(isTaskInProgress || isInQueue) && (
            <Stack sx={styles.loading}>
              <Typography fontWeight={600} fontSize={'26px'}>
                {isInQueue
                  ? 'Your video is in queue, getting closer to the spotlight!'
                  : 'Processing...'}
              </Typography>
              <CircularProgress />
              {!isInQueue && <CustomizedProgressBars value={cardProgress} sx={{ width: '80%' }} />}

              <Typography>
                Please wait while we create stunning visuals for your project.
              </Typography>
              <ProgressStuckNote />
            </Stack>
          )}

          {isTaskFailed && (
            <>
              <Typography sx={styles.heading}>Oops!</Typography>
              <ErrorOutlineIcon sx={styles.errorIcon} color="error" />
              <Typography sx={styles.errorMessage}>
                We encountered an issue! Your video couldn’t be created. Please try again.
              </Typography>
            </>
          )}
        </>
      </DialogContent>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', margin: 2 }}>
        {(isInQueue || isTaskInProgress) && (
          <Button
            variant="contained"
            sx={{
              color: (theme) => theme.palette.text.primary,
              width: '200px',
              boxShadow: 'none',
            }}
            onClick={onCloseDialog}
          >
            Minimize
          </Button>
        )}
        {isTaskFailed && (
          <Button
            variant="outlined"
            sx={{
              width: '200px',
            }}
            onClick={onCloseDialog}
          >
            Close
          </Button>
        )}
      </Box>
    </CustomDialogContainer>
  );
};

const styles: Styles = {
  title: {
    paddingBottom: '10px',
    paddingTop: '10px',
  },
  subTitle: {
    fontSize: '16px',
    fontWeight: 500,
  },
  dialogContent: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    backgroundColor: (theme) => theme.palette.background.secondary,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    padding: 4,
    gap: 2,
  },
  heading: { fontSize: '26px', fontWeight: 600, textAlign: 'center' },
  errorIcon: { width: 75, height: 75 },
  errorMessage: { color: (theme) => theme.palette.text.error, textAlign: 'center' },
};
